import { Link } from "react-router-dom";
import { CalendarDays, MapPin } from "lucide-react";

import { cn } from "@/lib/utils";
import { text } from "@/lib/memberDerive";
import { activityWhen, activityPlace, spotsText } from "@/lib/v3Views";
import { V3_PRIMARY } from "./v3";
import { V3_CARD, ActivityVisual, ParticipationPill, Pill } from "./v3app";

// V3 building blocks for activity views; sizes follow the 1448 px / 390 px frames.
export const activityHref = (a, rest = "") => `/aktivitaeten/${encodeURIComponent(a.id)}${rest}`;

function Facts({ a, big }) {
  return (
    <div className={`space-y-[9px] ${big ? "mt-[14px]" : "mt-[12px]"}`}>
      <p className={`flex items-center gap-[10px] text-v3-ink ${big ? "text-[15px]" : "text-[14px] lg:text-[15px]"}`}><CalendarDays className="h-[15px] w-[15px] shrink-0 text-v3-muted lg:hidden" aria-hidden="true" />{activityWhen(a)}</p>
      <p className="flex items-center gap-[10px] text-[13.5px] text-v3-muted lg:text-[14px]"><MapPin className="h-[15px] w-[15px] shrink-0 lg:hidden" aria-hidden="true" /><span className="min-w-0 break-words">{activityPlace(a)}</span></p>
    </div>
  );
}

// M11 card: image slot left (category area, no invented photo), facts right, one primary action.
// `status` replaces the participation pills (M22 "Nur gemerkt"); `children` adds secondary actions below.
export function ActivityListCard({ a, status, children }) {
  const title = text(a.title) || "Aktivität";
  const spots = spotsText(a);
  return (
    <article data-testid={`member-activity-${a.id}`} className={`${V3_CARD} grid gap-[14px] p-[10px] pb-[14px] sm:grid-cols-[minmax(0,144fr)_minmax(0,172fr)] lg:min-h-[256px] lg:gap-[18px] lg:pb-[10px]`}>
      <ActivityVisual a={a} className="h-[150px] sm:h-full lg:min-h-[236px]" />
      <div className="flex min-w-0 flex-col px-[6px] sm:px-0 sm:pr-[4px] sm:pt-[6px]">
        <h2 className="break-words text-[21px] font-bold leading-[28px] text-v3-ink lg:text-[26px] lg:leading-[34px]">{title}</h2>
        <Facts a={a} />
        <div className="mt-[12px] flex flex-wrap gap-2">
          {status || (<>
            {spots && <Pill testid={`activity-spots-${a.id}`}>{spots}</Pill>}
            <ParticipationPill a={a} testid={`activity-status-${a.id}`} />
          </>)}
        </div>
        {text(a.description) && <p className="mt-[10px] line-clamp-2 text-[14px] leading-[20px] text-v3-muted">{text(a.description)}</p>}
        <div className="mt-auto pt-[14px]"><Link to={activityHref(a)} data-testid={`activity-open-${a.id}`} className={cn(V3_PRIMARY, "lg:w-full")}>Details ansehen<span className="sr-only">: {title}</span></Link>{children}</div>
      </div>
    </article>
  );
}

// M12 head card: same facts as the list card, without a second link to itself.
export function ActivityHero({ a }) {
  const spots = spotsText(a);
  return (
    <div className={`${V3_CARD} mb-[11px] grid gap-[14px] p-[10px] pb-[16px] lg:grid-cols-[minmax(0,317fr)_minmax(0,377fr)] lg:gap-[19px] lg:pb-[10px]`} data-testid="activity-hero">
      <ActivityVisual a={a} className="h-[150px] lg:h-[280px]" />
      <div className="min-w-0 px-[6px] lg:px-0 lg:pt-[8px]">
        <p className="break-words text-[22px] font-bold leading-[30px] text-v3-ink lg:text-[26px] lg:leading-[34px]" data-testid="activity-detail-name">{text(a.title) || "Aktivität"}</p>
        <Facts a={a} big />
        <div className="mt-[12px] flex flex-wrap gap-2">{spots && <Pill tone="mint" testid="activity-capacity">{spots}</Pill>}<ParticipationPill a={a} testid="activity-detail-pill" /></div>
        {text(a.description) && <p className="mt-[12px] text-[14px] leading-[20px] text-v3-muted">{text(a.description)}</p>}
      </div>
    </div>
  );
}
